import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { gql, useQuery, useApolloClient, useMutation } from "@apollo/client";
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Skeleton,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  SimpleGrid,
  Switch,
  useToast,
  useClipboard,
  IconButton,
  Button,
  Input,
  FormControl,
  FormLabel,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Textarea,
} from "@chakra-ui/react";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { EditIcon, DownloadIcon, ViewIcon } from "@chakra-ui/icons";
import { useAuth } from "../../contexts/AuthContext";
import { pdf } from '@react-pdf/renderer';
import { BillPDF } from './components/BillPDF';
import { getColor, getComponent, brandConfig } from "../../brandConfig";
import { PaymentMethodSelector } from "./components/PaymentMethodSelector";
// import { LoginModal } from '../authentication';

enum BillStatus {
  DRAFT = "DRAFT",
  SENT = "SENT"
}

const GET_BILL = gql`
  query GetBillById($id: ID!) {
    bill(id: $id) {
      id
      isPaid
      status
      paymentMethod
      currency
      acceptCardPayment
      acceptCryptoPayment
      projectId
      issuedBy
      lineItems {
        id
        description
        amount
        createdAt
        updatedAt
      }
      totalAmount
      tenantId
      createdAt
      updatedAt
    }
  }
`;

const TOGGLE_BILL_PAYMENT_STATUS = gql`
  mutation ToggleBillPaymentStatus($id: ID!) {
    toggleBillPaymentStatus(id: $id) {
      id
      isPaid
    }
  }
`;

const UPDATE_BILL_STATUS = gql`
  mutation UpdateBillStatus($id: ID!, $status: BillStatus!) {
    updateBillStatus(id: $id, status: $status) {
      id
      status
      lineItems {
        id
        description
        amount
      }
      isPaid
      totalAmount
      paymentMethod
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_LINE_ITEM = gql`
  mutation UpdateLineItem($billId: ID!, $lineItemId: ID!, $amount: Float, $description: String) {
    updateLineItem(
      billId: $billId
      lineItemId: $lineItemId
      amount: $amount
      description: $description
    ) {
      id
      lineItems {
        id
        description
        amount
        createdAt
        updatedAt
      }
      status
      totalAmount
      isPaid
      updatedAt
    }
  }
`;

const UPDATE_BILL_PAYMENT_METHODS = gql`
  mutation UpdateBill($id: ID!, $input: BillInput!) {
    updateBill(id: $id, input: $input) {
      id
      acceptCardPayment
      acceptCryptoPayment
    }
  }
`;

const DUPLICATE_BILL_WITH_PERCENTAGE = gql`
  mutation DuplicateBillWithPercentage($id: ID!, $percentage: Float!, $paymentTerms: String) {
    duplicateBillWithPercentage(id: $id, percentage: $percentage, paymentTerms: $paymentTerms) {
      id
      isPaid
      status
      currency
      lineItems {
        id
        description
        amount
      }
      totalAmount
      percentageOfTotal
      paymentTerms
    }
  }
`;

const GET_CLIENT_BY_BILL = gql`
  query GetClientDetailsByBillId($billId: ID!) {
    getClientDetailsByBillId(billId: $billId) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
    }
  }
`;

const GET_BILL_ISSUER = gql`
  query GetBillIssuerDetails($billId: ID!) {
    getBillIssuerDetails(billId: $billId) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
      businessRegistrationNumber
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
      }
    }
  }
`;

const BillDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  // Brand colors setup
  const bg = getColor("background.main");
  const cardGradientBg = getColor("background.cardGradient");
  const cardBorder = getColor("border.darkCard");
  const textPrimary = getColor("text.primaryDark");
  const textSecondary = getColor("text.secondaryDark");
  const textMuted = getColor("text.mutedDark");

  // Check if user has admin permissions
  const isAdmin = user?.permissions?.includes("ADMIN") || user?.permissions?.includes("admin");

  // Rule: Display only the first 4 digits of the MongoDB ID for invoice ID
  const getDisplayBillId = (fullId: string) => {
    return fullId.substring(0, 4);
  };

  // Redirect unauthenticated users to preview page
  React.useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate(`/bill/${id}/preview`);
    }
  }, [authLoading, isAuthenticated, navigate, id]);

  const { loading, error, data } = useQuery(GET_BILL, {
    variables: { id },
  });

  const { data: clientData, loading: clientLoading } = useQuery(GET_CLIENT_BY_BILL, {
    variables: { billId: id },
  });

  const { data: issuerData, loading: issuerLoading } = useQuery(GET_BILL_ISSUER, {
    variables: { billId: id },
  });

  console.log("Raw data:", data);
  console.log("Bill:", data?.bill);
  console.log("Line Items:", data?.bill?.lineItems);
  console.log("[BillDetails] Issuer crypto wallets:", issuerData?.getBillIssuerDetails?.paymentReceivingDetails?.cryptoWallets);
  console.log("[BillDetails] Bitcoin wallet found:", issuerData?.getBillIssuerDetails?.paymentReceivingDetails?.cryptoWallets?.find(
    (wallet: any) => wallet.network === "BTC"
  ));

  const client = useApolloClient();
  const toast = useToast();

  const [updateBillStatus] = useMutation(UPDATE_BILL_STATUS);
  const [updateLineItem] = useMutation(UPDATE_LINE_ITEM);
  const [updateBillPaymentMethods] = useMutation(UPDATE_BILL_PAYMENT_METHODS);
  const [duplicateBillWithPercentage] = useMutation(DUPLICATE_BILL_WITH_PERCENTAGE);

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editValues, setEditValues] = React.useState<{
    description: string;
    amount: number;
  }>({ description: "", amount: 0 });

  // Duplicate bill modal state
  const { isOpen: isDuplicateModalOpen, onOpen: openDuplicateModal, onClose: closeDuplicateModal } = useDisclosure();
  const [duplicatePercentage, setDuplicatePercentage] = React.useState(50);
  const [duplicatePaymentTerms, setDuplicatePaymentTerms] = React.useState("");

  // const { isOpen, onOpen, onClose } = useDisclosure();
  // const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  // React.useEffect(() => {
  //   const authToken = localStorage.getItem('auth_token');
  //   if (!authToken) {
  //     onOpen();
  //   } else {
  //     setIsAuthenticated(true);
  //   }
  // }, [onOpen]);

  const handleTogglePayment = async (billId: string) => {
    try {
      await client.mutate({
        mutation: TOGGLE_BILL_PAYMENT_STATUS,
        variables: { id: billId },
        refetchQueries: [{ query: GET_BILL, variables: { id: billId } }]
      });

      toast({
        title: `Bill marked as ${data?.bill.isPaid ? "unpaid" : "paid"}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error toggling payment status:", error);
      toast({
        title: "Error updating payment status",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUpdatePaymentMethods = async (acceptCard: boolean, acceptCrypto: boolean) => {
    try {
      await updateBillPaymentMethods({
        variables: {
          id: data.bill.id,
          input: {
            acceptCardPayment: acceptCard,
            acceptCryptoPayment: acceptCrypto,
            projectId: data.bill.projectId,
            isPaid: data.bill.isPaid,
            currency: data.bill.currency || "AUD"
          }
        },
        refetchQueries: [{ query: GET_BILL, variables: { id: data.bill.id } }]
      });

      toast({
        title: "Payment methods updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error updating payment methods:", error);
      toast({
        title: "Error updating payment methods",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDuplicateBill = async () => {
    try {
      const paymentTermsText = duplicatePaymentTerms || `${duplicatePercentage}% upfront payment`;
      
      const { data: duplicateData } = await duplicateBillWithPercentage({
        variables: {
          id: data.bill.id,
          percentage: duplicatePercentage,
          paymentTerms: paymentTermsText
        }
      });

      toast({
        title: "Bill duplicated successfully",
        description: `Created ${duplicatePercentage}% partial bill`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      closeDuplicateModal();
      
      // Navigate to the new bill
      navigate(`/bill/${duplicateData.duplicateBillWithPercentage.id}`);
    } catch (error) {
      console.error("Error duplicating bill:", error);
      toast({
        title: "Error duplicating bill",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSendBill = async () => {
    try {
      await updateBillStatus({
        variables: {
          id: data.bill.id,
          status: BillStatus.SENT
        },
        refetchQueries: [{ query: GET_BILL, variables: { id: data.bill.id } }]
      });

      toast({
        title: "Bill sent successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error sending bill",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const doc = <BillPDF bill={bill} clientData={clientData} issuerData={issuerData} />;
      const pdfBlob = await pdf(doc).toBlob();

      // Create download link
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${getDisplayBillId(bill.id)}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "PDF downloaded successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error generating PDF",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handlePreviewPDF = () => {
    navigate(`/bill/${id}/preview`);
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <>
        <NavbarWithCallToAction />
        <Container maxW="container.xl" py={8}>
          <VStack spacing={6}>
            <Skeleton height="60px" width="100%" />
            <Skeleton height="200px" width="100%" />
            <Skeleton height="300px" width="100%" />
          </VStack>
        </Container>
        <FooterWithFourColumns />
      </>
    );
  }

  // Don't render if unauthenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <>
        <NavbarWithCallToAction />
        <Container maxW="container.xl" py={8}>
          <VStack spacing={6}>
            <Skeleton height="60px" width="100%" />
            <Skeleton height="200px" width="100%" />
            <Skeleton height="300px" width="100%" />
          </VStack>
        </Container>
        <FooterWithFourColumns />
      </>
    );
  }

  if (error) {
    return (
      <>
        <NavbarWithCallToAction />
        <Container maxW="container.xl" py={8}>
          <Alert status="error">
            <AlertIcon />
            Error loading bill details: {error.message}
          </Alert>
        </Container>
      </>
    );
  }

  const bill = data?.bill;
  if (!bill) {
    return (
      <>
        <NavbarWithCallToAction />
        <Container maxW="container.xl" py={8}>
          <Alert status="info">
            <AlertIcon />
            Bill not found
          </Alert>
        </Container>
      </>
    );
  }

  const formatDate = (dateString: string | { $date: string }) => {
    const date = dateString instanceof Object ? dateString.$date : dateString;
    return new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  };

  return (
    <Box minH="100vh" bg={bg}>
      <NavbarWithCallToAction />
      {/* {!isAuthenticated ? (
        <>
          <Container maxW="container.xl" py={8}>
            <Alert status="warning">
              <AlertIcon />
              Please log in to view bill details
            </Alert>
          </Container>
          <LoginModal 
            isOpen={isOpen} 
            onClose={() => {
              onClose();
              const authToken = localStorage.getItem('auth_token');
              if (authToken) {
                setIsAuthenticated(true);
              }
            }} 
          />
        </>
      ) : ( */}
      <Container maxW="container.xl" py={8}>
        <VStack spacing={6} align="stretch">
          <Card
            bg={cardGradientBg}
            borderColor={cardBorder}
            borderWidth="1px"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            backdropFilter="blur(10px)">
            <CardHeader>
              <VStack spacing={4} width="100%">
                <HStack justify="space-between" width="100%">
                  <VStack align="start" spacing={2}>
                    <Heading size={{ base: "md", md: "lg" }} color={brandConfig.colors.text.inverse}>Bill Details</Heading>
                    <Text color={brandConfig.colors.text.mutedDark} fontSize={{ base: "sm", md: "md" }}>Invoice ID: {getDisplayBillId(bill.id)}</Text>
                  </VStack>
                  <HStack spacing={{ base: 2, md: 4 }} flexWrap="wrap">
                    {bill.status === BillStatus.DRAFT && isAdmin && (
                      <Button
                        colorScheme="blue"
                        size={{ base: "sm", md: "md" }}
                        onClick={() => {
                          setDuplicatePaymentTerms(`${duplicatePercentage}% upfront payment`);
                          openDuplicateModal();
                        }}
                      >
                        Duplicate (Partial)
                      </Button>
                    )}
                    <Button
                      colorScheme="purple"
                      size={{ base: "sm", md: "md" }}
                      onClick={handlePreviewPDF}
                      leftIcon={<ViewIcon />}
                    >
                      Preview PDF
                    </Button>
                    <Button
                      colorScheme="green"
                      size={{ base: "sm", md: "md" }}
                      onClick={handleDownloadPDF}
                      leftIcon={<DownloadIcon />}
                    >
                      Download PDF
                    </Button>
                    {isAdmin && (
                      <Button
                        colorScheme="blue"
                        size={{ base: "sm", md: "md" }}
                        onClick={handleSendBill}
                        isDisabled={bill.status === BillStatus.SENT}
                      >
                        Send Bill
                      </Button>
                    )}
                    <Badge
                      fontSize={{ base: "md", md: "lg" }}
                      colorScheme={bill.status === BillStatus.SENT ? "blue" : "gray"}
                      p={2}
                      borderRadius="md"
                    >
                      {bill.status}
                    </Badge>
                  </HStack>
                </HStack>

                <Card p={6} width="100%" bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px" borderRadius="lg">
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                    <VStack align="start" spacing={4}>
                      <HStack justify="flex-start" spacing={4} flexWrap="wrap">
                        <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color={textPrimary}>Payment Status:</Text>
                        <Switch
                          size="lg"
                          isChecked={bill.isPaid}
                          onChange={() => handleTogglePayment(bill.id)}
                          colorScheme="green"
                          sx={{
                            "span.chakra-switch__track": {
                              w: "4rem",
                              h: "2rem",
                            },
                            "span.chakra-switch__thumb": {
                              w: "1.75rem",
                              h: "1.75rem",
                            }
                          }}
                        />
                        <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color={bill.isPaid ? brandConfig.colors.status.success : brandConfig.colors.text.mutedDark}>
                          {bill.isPaid ? "PAID" : "UNPAID"}
                        </Text>
                      </HStack>
                    </VStack>
                  </SimpleGrid>
                </Card>
              </VStack>
            </CardHeader>

            <CardBody>
              <VStack spacing={6} align="stretch">
                <Box>
                  <Heading size="md" mb={4} color={textPrimary}>Bill Information</Heading>
                  <Card variant="outline" bg={cardGradientBg} borderColor={cardBorder}>
                    <CardBody>
                      <SimpleGrid columns={2} spacing={4}>
                        <Box>
                          <Text fontWeight="bold" color={textSecondary}>Created Date</Text>
                          <Text color={brandConfig.colors.text.inverse}>{formatDate(bill.createdAt)}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" color={textSecondary}>Last Updated</Text>
                          <Text color={brandConfig.colors.text.inverse}>{formatDate(bill.updatedAt)}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" color={textSecondary}>Payment Method</Text>
                          <Text color={brandConfig.colors.text.inverse}>{bill.paymentMethod || "Not specified"}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" color={textSecondary}>Total Amount</Text>
                          <Text color={brandConfig.colors.text.inverse}>
                            ${(bill.totalAmount && bill.totalAmount > 0) ?
                              bill.totalAmount.toFixed(2) :
                              (bill.lineItems?.reduce((total: number, item: any) => total + Number(item.amount), 0)?.toFixed(2)) ||
                              "0.00"} {bill.currency || "AUD"}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" color={textSecondary}>Tenant ID</Text>
                          <Text color={brandConfig.colors.text.inverse}>{bill.tenantId}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" color={textSecondary}>Project ID</Text>
                          {bill.projectId ? (
                            <Text
                              color="blue.500"
                              textDecoration="underline"
                              cursor="pointer"
                              _hover={{ color: "blue.700" }}
                              onClick={() => window.open(`/project/${bill.projectId}`, '_blank')}
                            >
                              {bill.projectId}
                            </Text>
                          ) : (
                            <Text color={brandConfig.colors.text.inverse}>Not associated with a project</Text>
                          )}
                        </Box>
                      </SimpleGrid>
                      
                      {/* Payment Method Toggles - Only visible for DRAFT bills */}
                      {bill.status === BillStatus.DRAFT && isAdmin && (
                        <>
                          <Divider my={4} borderColor={cardBorder} />
                          <Box>
                            <Text fontSize="sm" fontWeight="bold" color={textPrimary} mb={3}>
                              Payment Methods Available to Customer
                            </Text>
                            <VStack align="stretch" spacing={3}>
                              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                <FormLabel htmlFor="card-payment-edit" mb="0" color={textSecondary}>
                                  Accept Card Payments (Stripe)
                                </FormLabel>
                                <Switch
                                  id="card-payment-edit"
                                  isChecked={bill.acceptCardPayment ?? true}
                                  onChange={(e) => handleUpdatePaymentMethods(e.target.checked, bill.acceptCryptoPayment ?? false)}
                                  colorScheme="blue"
                                />
                              </FormControl>

                              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                <FormLabel htmlFor="crypto-payment-edit" mb="0" color={textSecondary}>
                                  Accept Crypto Payments (Bitcoin)
                                </FormLabel>
                                <Switch
                                  id="crypto-payment-edit"
                                  isChecked={bill.acceptCryptoPayment ?? false}
                                  onChange={(e) => handleUpdatePaymentMethods(bill.acceptCardPayment ?? true, e.target.checked)}
                                  colorScheme="blue"
                                />
                              </FormControl>
                            </VStack>
                            <Text fontSize="xs" color={textMuted} mt={2}>
                              These options can only be changed while the bill is in DRAFT status
                            </Text>
                          </Box>
                        </>
                      )}

                      {/* Display payment methods for non-draft bills */}
                      {bill.status !== BillStatus.DRAFT && (
                        <Box mt={4}>
                          <Text fontSize="sm" fontWeight="bold" color={textPrimary} mb={2}>
                            Available Payment Methods:
                          </Text>
                          <HStack spacing={3}>
                            {(bill.acceptCardPayment ?? true) && (
                              <Badge colorScheme="blue">Card Payment</Badge>
                            )}
                            {bill.acceptCryptoPayment && (
                              <Badge colorScheme="orange">Crypto Payment</Badge>
                            )}
                            {!bill.acceptCardPayment && !bill.acceptCryptoPayment && (
                              <Text fontSize="sm" color={textMuted}>Bank Transfer Only</Text>
                            )}
                          </HStack>
                        </Box>
                      )}
                    </CardBody>
                  </Card>
                </Box>

                <Box>
                  <Heading size="md" mb={4} color={textPrimary}>Client Information</Heading>
                  <Card variant="outline" bg={cardGradientBg} borderColor={cardBorder}>
                    <CardBody>
                      {clientLoading ? (
                        <Skeleton height="100px" />
                      ) : clientData?.getClientDetailsByBillId ? (
                        <SimpleGrid columns={2} spacing={4}>
                          <Box>
                            <Text fontWeight="bold" color={textSecondary}>Name</Text>
                            <Text color={brandConfig.colors.text.inverse}>{clientData.getClientDetailsByBillId.fName} {clientData.getClientDetailsByBillId.lName}</Text>
                          </Box>
                          <Box>
                            <Text fontWeight="bold" color={textSecondary}>Email</Text>
                            <Text color={brandConfig.colors.text.inverse}>{clientData.getClientDetailsByBillId.email}</Text>
                          </Box>
                          <Box>
                            <Text fontWeight="bold" color={textSecondary}>Phone</Text>
                            <Text color={brandConfig.colors.text.inverse}>{clientData.getClientDetailsByBillId.phoneNumber}</Text>
                          </Box>
                          <Box>
                            <Text fontWeight="bold" color={textSecondary}>Business Name</Text>
                            <Text color={brandConfig.colors.text.inverse}>{clientData.getClientDetailsByBillId.businessName || "Not provided"}</Text>
                          </Box>
                        </SimpleGrid>
                      ) : (
                        <Text color={brandConfig.colors.text.mutedDark}>No client information available</Text>
                      )}
                    </CardBody>
                  </Card>
                </Box>

                <Box>
                  <Heading size="md" mb={4} color={textPrimary}>Bill Issued By</Heading>
                  <Card variant="outline" bg={cardGradientBg} borderColor={cardBorder}>
                    <CardBody>
                      {issuerLoading ? (
                        <Skeleton height="200px" />
                      ) : issuerData?.getBillIssuerDetails ? (
                        <VStack spacing={6} align="stretch">
                          {/* Business Information */}
                          <Box>
                            <Text fontWeight="bold" fontSize="lg" mb={3} color={brandConfig.colors.text.inverse}>Business Information</Text>
                            <SimpleGrid columns={2} spacing={4}>
                              <Box>
                                <Text fontWeight="bold" color={textSecondary}>Business Name</Text>
                                <Text color={brandConfig.colors.text.inverse}>
                                  {brandConfig.contact.businessName || issuerData.getBillIssuerDetails.businessName || "Not provided"}
                                </Text>
                              </Box>
                              {issuerData.getBillIssuerDetails.businessRegistrationNumber && (
                                <Box>
                                  <Text fontWeight="bold" color={textSecondary}>Registration Number</Text>
                                  <Text color={brandConfig.colors.text.inverse}>{issuerData.getBillIssuerDetails.businessRegistrationNumber}</Text>
                                </Box>
                              )}
                              <Box>
                                <Text fontWeight="bold" color={textSecondary}>Contact Person</Text>
                                <Text color={brandConfig.colors.text.inverse}>
                                  {brandConfig.contact.contactName || `${issuerData.getBillIssuerDetails.fName} ${issuerData.getBillIssuerDetails.lName}`}
                                </Text>
                              </Box>
                              <Box>
                                <Text fontWeight="bold" color={textSecondary}>Email</Text>
                                <Text color={brandConfig.colors.text.inverse}>
                                  {brandConfig.contact.email || issuerData.getBillIssuerDetails.email}
                                </Text>
                              </Box>
                              <Box>
                                <Text fontWeight="bold" color={textSecondary}>Phone</Text>
                                <Text color={brandConfig.colors.text.inverse}>
                                  {brandConfig.contact.phone || issuerData.getBillIssuerDetails.phoneNumber || "Not provided"}
                                </Text>
                              </Box>
                            </SimpleGrid>
                          </Box>

                          {/* Payment Information - Hidden by default for cleaner UI */}
                          {false && issuerData.getBillIssuerDetails.paymentReceivingDetails && (
                            <Box>
                              <Text fontWeight="bold" fontSize="lg" mb={3} color={brandConfig.colors.text.inverse}>Payment Information</Text>

                              {/* Bank Account Details */}
                              {issuerData.getBillIssuerDetails.paymentReceivingDetails.bankAccount && (
                                <Box p={4} bg={cardGradientBg} borderRadius="md" mb={4} border="1px" borderColor={cardBorder}>
                                  <Text fontWeight="bold" mb={2} color={brandConfig.colors.text.inverse}>🏦 Bank Account Details</Text>
                                  <SimpleGrid columns={2} spacing={4}>
                                    <Box>
                                      <Text fontWeight="semibold" color={textSecondary}>Account Name</Text>
                                      <HStack>
                                        <Text color={brandConfig.colors.text.inverse}>{issuerData.getBillIssuerDetails.paymentReceivingDetails.bankAccount.accountName}</Text>
                                        <Button size="xs" onClick={() => navigator.clipboard.writeText(issuerData.getBillIssuerDetails.paymentReceivingDetails.bankAccount.accountName)}>
                                          Copy
                                        </Button>
                                      </HStack>
                                    </Box>
                                    <Box>
                                      <Text fontWeight="semibold" color={textSecondary}>BSB</Text>
                                      <HStack>
                                        <Text color={brandConfig.colors.text.inverse}>{issuerData.getBillIssuerDetails.paymentReceivingDetails.bankAccount.bsb}</Text>
                                        <Button size="xs" onClick={() => navigator.clipboard.writeText(issuerData.getBillIssuerDetails.paymentReceivingDetails.bankAccount.bsb)}>
                                          Copy
                                        </Button>
                                      </HStack>
                                    </Box>
                                    <Box>
                                      <Text fontWeight="semibold" color={textSecondary}>Account Number</Text>
                                      <HStack>
                                        <Text color={brandConfig.colors.text.inverse}>{issuerData.getBillIssuerDetails.paymentReceivingDetails.bankAccount.accountNumber}</Text>
                                        <Button size="xs" onClick={() => navigator.clipboard.writeText(issuerData.getBillIssuerDetails.paymentReceivingDetails.bankAccount.accountNumber)}>
                                          Copy
                                        </Button>
                                      </HStack>
                                    </Box>
                                    {issuerData.getBillIssuerDetails.paymentReceivingDetails.bankAccount.bankName && (
                                      <Box>
                                        <Text fontWeight="semibold" color={textSecondary}>Bank Name</Text>
                                        <Text color={brandConfig.colors.text.inverse}>{issuerData.getBillIssuerDetails.paymentReceivingDetails.bankAccount.bankName}</Text>
                                      </Box>
                                    )}
                                    {issuerData.getBillIssuerDetails.paymentReceivingDetails.bankAccount.swiftCode && (
                                      <Box>
                                        <Text fontWeight="semibold" color={textSecondary}>SWIFT Code</Text>
                                        <HStack>
                                          <Text color={brandConfig.colors.text.inverse}>{issuerData.getBillIssuerDetails.paymentReceivingDetails.bankAccount.swiftCode}</Text>
                                          <Button size="xs" onClick={() => navigator.clipboard.writeText(issuerData.getBillIssuerDetails.paymentReceivingDetails.bankAccount.swiftCode)}>
                                            Copy
                                          </Button>
                                        </HStack>
                                      </Box>
                                    )}
                                  </SimpleGrid>
                                </Box>
                              )}

                              {/* Crypto Wallets */}
                              {issuerData.getBillIssuerDetails.paymentReceivingDetails.cryptoWallets?.length > 0 && (
                                <Box p={4} bg={cardGradientBg} borderRadius="md" mb={4} border="1px" borderColor={cardBorder}>
                                  <Text fontWeight="bold" mb={2} color={brandConfig.colors.text.inverse}>₿ Cryptocurrency Wallets</Text>
                                  <VStack spacing={3}>
                                    {issuerData.getBillIssuerDetails.paymentReceivingDetails.cryptoWallets.map((wallet: any, index: number) => (
                                      <Box key={index} p={3} bg={cardGradientBg} borderRadius="md" border="1px" borderColor={cardBorder} width="100%">
                                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                          <Box>
                                            <Text fontSize="sm" fontWeight="semibold" color={brandConfig.colors.text.secondaryDark}>Network</Text>
                                            <Badge colorScheme="purple">{wallet.network}</Badge>
                                          </Box>
                                          <Box>
                                            <Text fontSize="sm" fontWeight="semibold" color={brandConfig.colors.text.secondaryDark}>Wallet Address</Text>
                                            <HStack>
                                              <Text fontSize="sm" fontFamily="mono" color={brandConfig.colors.text.inverse}>{wallet.walletAddress}</Text>
                                              <Button size="xs" onClick={() => navigator.clipboard.writeText(wallet.walletAddress)}>
                                                Copy
                                              </Button>
                                            </HStack>
                                          </Box>
                                          {wallet.memo && (
                                            <Box>
                                              <Text fontSize="sm" fontWeight="semibold" color={brandConfig.colors.text.secondaryDark}>Memo</Text>
                                              <Text fontSize="sm" color={brandConfig.colors.text.inverse}>{wallet.memo}</Text>
                                            </Box>
                                          )}
                                        </SimpleGrid>
                                      </Box>
                                    ))}
                                  </VStack>
                                </Box>
                              )}

                              {/* PayPal */}
                              {issuerData.getBillIssuerDetails.paymentReceivingDetails.paypalEmail && (
                                <Box p={4} bg={cardGradientBg} borderRadius="md" mb={4} border="1px" borderColor={cardBorder}>
                                  <Text fontWeight="bold" mb={2} color={brandConfig.colors.text.inverse}>💰 PayPal</Text>
                                  <HStack>
                                    <Text color={brandConfig.colors.text.inverse}>{issuerData.getBillIssuerDetails.paymentReceivingDetails.paypalEmail}</Text>
                                    <Button size="xs" onClick={() => navigator.clipboard.writeText(issuerData.getBillIssuerDetails.paymentReceivingDetails.paypalEmail)}>
                                      Copy
                                    </Button>
                                  </HStack>
                                </Box>
                              )}

                              {/* Stripe Connect */}
                              {issuerData.getBillIssuerDetails.paymentReceivingDetails.stripeConnect && (
                                <Box p={4} bg={cardGradientBg} borderRadius="md" mb={4} border="1px" borderColor={cardBorder}>
                                  <Text fontWeight="bold" mb={2} color={brandConfig.colors.text.inverse}>💳 Stripe Connect</Text>
                                  <HStack spacing={4}>
                                    <Badge colorScheme={issuerData.getBillIssuerDetails.paymentReceivingDetails.stripeConnect.accountVerified ? "green" : "yellow"}>
                                      {issuerData.getBillIssuerDetails.paymentReceivingDetails.stripeConnect.accountVerified ? "✅ Verified" : "⏳ Pending Verification"}
                                    </Badge>
                                    <Text fontSize="sm" color={brandConfig.colors.text.secondaryDark}>
                                      Available for credit card payments
                                    </Text>
                                  </HStack>
                                </Box>
                              )}

                              {/* Accepted Payment Methods */}
                              <Box>
                                <Text fontWeight="semibold" mb={2} color={brandConfig.colors.text.inverse}>Accepted Payment Methods</Text>
                                <HStack spacing={2} flexWrap="wrap">
                                  {issuerData.getBillIssuerDetails.paymentReceivingDetails.acceptedMethods?.map((method: string) => (
                                    <Badge key={method} colorScheme="blue" variant="solid">
                                      {method === "BANK_TRANSFER" ? "🏦 Bank Transfer" :
                                        method === "CRYPTO" ? "₿ Cryptocurrency" :
                                          method === "STRIPE" ? "💳 Credit Cards" :
                                            method === "PAYPAL" ? "💰 PayPal" : method}
                                    </Badge>
                                  ))}
                                </HStack>
                              </Box>
                            </Box>
                          )}
                        </VStack>
                      ) : (
                        <Text color={brandConfig.colors.text.mutedDark}>No issuer information available</Text>
                      )}
                    </CardBody>
                  </Card>
                </Box>

                <Box>
                  <Heading size="md" mb={4} color={textPrimary}>Line Items</Heading>
                  <Card variant="outline" bg={cardGradientBg} borderColor={cardBorder}>
                    <CardBody>
                      <Box overflowX="auto" width="100%">
                        <Table variant="simple" size={{ base: "sm", md: "md" }}>
                          <Thead>
                            <Tr>
                              <Th color={textSecondary}>Description</Th>
                              <Th isNumeric color={textSecondary}>Amount ({bill.currency || "AUD"})</Th>
                              <Th color={textSecondary}>{isAdmin ? "Actions" : ""}</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {bill.lineItems && bill.lineItems.map((item: any) => {
                              const isEditing = editingId === (item.id);

                              const handleEdit = () => {
                                console.log("Setting edit ID to:", item.id);
                                setEditingId(item.id);
                                setEditValues({
                                  description: item.description,
                                  amount: item.amount
                                });
                              };

                              const handleSave = async () => {
                                try {
                                  await updateLineItem({
                                    variables: {
                                      billId: bill.id,
                                      lineItemId: item.id,
                                      description: editValues.description,
                                      amount: parseFloat(editValues.amount.toString())
                                    },
                                    refetchQueries: [{ query: GET_BILL, variables: { id: bill.id } }]
                                  });

                                  setEditingId(null);
                                  toast({
                                    title: "Line item updated",
                                    status: "success",
                                    duration: 3000,
                                    isClosable: true,
                                  });
                                } catch (error) {
                                  toast({
                                    title: "Error updating line item",
                                    description: error instanceof Error ? error.message : "Unknown error occurred",
                                    status: "error",
                                    duration: 5000,
                                    isClosable: true,
                                  });
                                }
                              };

                              return (
                                <Tr key={item.id}>
                                  <Td maxW={{ base: "150px", md: "300px" }} overflow="hidden" textOverflow="ellipsis">
                                    {isEditing ? (
                                      <Input
                                        value={editValues.description}
                                        onChange={(e) => setEditValues(prev => ({
                                          ...prev,
                                          description: e.target.value
                                        }))}
                                        size={{ base: "sm", md: "md" }}
                                      />
                                    ) : (
                                      <Text color={brandConfig.colors.text.inverse}>{item.description}</Text>
                                    )}
                                  </Td>
                                  <Td isNumeric>
                                    {isEditing ? (
                                      <Input
                                        type="number"
                                        value={editValues.amount}
                                        onChange={(e) => setEditValues(prev => ({
                                          ...prev,
                                          amount: parseFloat(e.target.value)
                                        }))}
                                        textAlign="right"
                                        size={{ base: "sm", md: "md" }}
                                      />
                                    ) : (
                                      <Text color={brandConfig.colors.text.inverse}>${Number(item.amount).toFixed(2)}</Text>
                                    )}
                                  </Td>
                                  <Td>
                                    {isAdmin ? (
                                      <HStack spacing={2}>
                                        {isEditing ? (
                                          <>
                                            <Button
                                              size="sm"
                                              colorScheme="green"
                                              onClick={handleSave}
                                              px={{ base: 2, md: 4 }}
                                            >
                                              Save
                                            </Button>
                                            <Button
                                              size="sm"
                                              onClick={() => setEditingId(null)}
                                              px={{ base: 2, md: 4 }}
                                            >
                                              Cancel
                                            </Button>
                                          </>
                                        ) : (
                                          <IconButton
                                            aria-label="Edit line item"
                                            icon={<EditIcon />}
                                            size="sm"
                                            colorScheme="blue"
                                            onClick={handleEdit}
                                          />
                                        )}
                                      </HStack>
                                    ) : (
                                      <Text color={brandConfig.colors.text.mutedDark} fontSize="sm">-</Text>
                                    )}
                                  </Td>
                                </Tr>
                              );
                            })}
                          </Tbody>
                        </Table>

                        {/* Total Summary */}
                        <HStack justify="flex-end" mt={6} bg={cardGradientBg} p={4} borderRadius="md" border="1px" borderColor={cardBorder}>
                          <Text fontWeight="bold" fontSize="lg" color={brandConfig.colors.text.inverse}>Total:</Text>
                          <Text fontWeight="bold" fontSize="lg" color={brandConfig.colors.text.inverse}>
                            ${(bill.totalAmount && bill.totalAmount > 0) ?
                              bill.totalAmount.toFixed(2) :
                              (bill.lineItems?.reduce((total: number, item: any) => total + Number(item.amount), 0)?.toFixed(2)) ||
                              "0.00"} {bill.currency || "AUD"}
                          </Text>
                        </HStack>
                      </Box>
                    </CardBody>
                  </Card>
                </Box>

                {/* Payment Options - Placed after all bill details */}
                {!bill.isPaid && (
                  <Box>
                    <Card
                      variant="outline"
                      borderColor="blue.500"
                      borderWidth="2px"
                      bg={cardGradientBg}
                      boxShadow="0 4px 20px 0 rgba(59, 130, 246, 0.15)"
                    >
                      <CardBody>
                        <PaymentMethodSelector
                          billId={bill.id}
                          amount={
                            (bill.totalAmount && bill.totalAmount > 0)
                              ? bill.totalAmount
                              : (bill.lineItems?.reduce((total: number, item: any) => total + Number(item.amount), 0) || 0)
                          }
                          acceptCardPayment={bill.acceptCardPayment ?? true}
                          acceptCryptoPayment={bill.acceptCryptoPayment ?? false}
                          issuerBankDetails={
                            issuerData?.getBillIssuerDetails?.paymentReceivingDetails?.bankAccount
                          }
                          issuerWalletAddress={
                            issuerData?.getBillIssuerDetails?.paymentReceivingDetails?.cryptoWallets?.find(
                              (wallet: any) => wallet.network === "BTC"
                            )?.walletAddress
                          }
                          cryptoDiscountPercentage={
                            issuerData?.getBillIssuerDetails?.paymentReceivingDetails?.cryptoDiscountPercentage
                          }
                          onPaymentComplete={() => {
                            console.log('[BillDetails] Payment completed, refreshing bill data');
                            // Refresh the bill data to update payment status
                            client.refetchQueries({
                              include: [GET_BILL],
                            });
                            toast({
                              title: "Payment Successful",
                              description: "Your payment has been processed successfully.",
                              status: "success",
                              duration: 5000,
                              isClosable: true,
                            });
                          }}
                        />
                      </CardBody>
                    </Card>
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Container>
      {/* )} */}
      
      {/* Duplicate Bill Modal */}
      <Modal isOpen={isDuplicateModalOpen} onClose={closeDuplicateModal} size="xl">
        <ModalOverlay />
        <ModalContent bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px">
          <ModalHeader color={textPrimary}>Create Partial Bill</ModalHeader>
          <ModalCloseButton color={textPrimary} />
          <ModalBody>
            <VStack spacing={6} align="stretch">
              <Box>
                <FormLabel color={textPrimary} mb={3}>
                  Percentage of Total: {duplicatePercentage}%
                </FormLabel>
                <Slider
                  value={duplicatePercentage}
                  onChange={setDuplicatePercentage}
                  min={10}
                  max={100}
                  step={5}
                  colorScheme="blue"
                >
                  <SliderTrack bg="gray.600">
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb boxSize={6} />
                </Slider>
                <HStack justify="space-between" mt={2}>
                  <Text fontSize="sm" color={textMuted}>10%</Text>
                  <Text fontSize="sm" color={textMuted}>50%</Text>
                  <Text fontSize="sm" color={textMuted}>100%</Text>
                </HStack>
              </Box>

              <Box>
                <FormLabel color={textPrimary}>Payment Terms (Optional)</FormLabel>
                <Textarea
                  placeholder={`${duplicatePercentage}% upfront payment`}
                  value={duplicatePaymentTerms}
                  onChange={(e) => setDuplicatePaymentTerms(e.target.value)}
                  bg="rgba(0, 0, 0, 0.2)"
                  borderColor={cardBorder}
                  color={textPrimary}
                  _placeholder={{ color: textMuted }}
                  _hover={{ borderColor: textSecondary }}
                  rows={2}
                />
              </Box>

              <Box p={4} bg="rgba(0, 0, 0, 0.3)" borderRadius="md">
                <Text color={textPrimary} fontWeight="bold" mb={3}>
                  New Bill Preview:
                </Text>
                <VStack align="stretch" spacing={2}>
                  {bill?.lineItems?.map((item: any, index: number) => (
                    <HStack key={index} justify="space-between">
                      <Text color={textSecondary} fontSize="sm" noOfLines={1}>
                        {item.description}
                      </Text>
                      <Text color={textPrimary} fontWeight="bold">
                        ${((item.amount * duplicatePercentage) / 100).toFixed(2)}
                      </Text>
                    </HStack>
                  ))}
                  <Divider borderColor={cardBorder} />
                  <HStack justify="space-between">
                    <Text color={textPrimary} fontWeight="bold">Total:</Text>
                    <Text color={textPrimary} fontWeight="bold" fontSize="lg">
                      ${(() => {
                        // Calculate total from line items if totalAmount is not available
                        const originalTotal = bill?.totalAmount && bill.totalAmount > 0 
                          ? bill.totalAmount 
                          : bill?.lineItems?.reduce((sum: number, item: any) => sum + Number(item.amount), 0) || 0;
                        return (originalTotal * duplicatePercentage / 100).toFixed(2);
                      })()} {bill?.currency || 'AUD'}
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeDuplicateModal} color={textPrimary}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleDuplicateBill}>
              Create {duplicatePercentage}% Bill
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <FooterWithFourColumns />
    </Box>
  );
};

export default BillDetails; 